import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { GlobalService } from './global.service';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Observable, Subject, of, throwError } from 'rxjs';
import { BulkReveal, ICampaignStage } from '../model/campaign';
import { CreditActivityService } from './credit-activity.service';

const devUrl = environment.backendurl;

@Injectable({
  providedIn: 'root'
})
export class CampaignService {
  constructor(private http: HttpClient, private creditActivityService: CreditActivityService) {}

  private contactSliderToListingSubject: Subject<any> = new Subject();

  getContactSliderData(): Observable<any>{
    return this.contactSliderToListingSubject.asObservable();
  }
  setContactSliderToListingData(data: any){
    this.contactSliderToListingSubject.next(data);
  }


  objectToQueryString(obj) {
    const keys = Object.keys(obj);
    let keyValuePairs = keys.map(key => {
      if(key.includes('[]')){
        if(Array.isArray(obj[key]) && obj[key].length > 0){
          return obj[key].map(v=>{
            return encodeURIComponent(key)+'='+encodeURIComponent(v);
          })
        }
    }
    else
      return encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]);
    });
    keyValuePairs =  keyValuePairs.filter(x=> x!==undefined);
    return keyValuePairs.join('&').replace(/\,/g, "&").replace(/\%5B%5D/g, "");
  }

  // Subject for notifying changes in campaigns
  public campaignUpdated: Subject<void> = new Subject<void>();

  // Campaigns
  getCampaigns(data) {
    const query = "/campaigns" + "?" +  this.objectToQueryString(data);
    let url = devUrl + query;
    return this.http.get(url).pipe(
      map(response => response),
      catchError(GlobalService.handleError('Get Campaigns', url))
    );
  }

  getCampaignStats(campaignId: number, data) {
    const query = `/campaigns/${campaignId}/stats` + "?" +  this.objectToQueryString(data);
    let url = devUrl + query;
    return this.http.get(url).pipe(
      map(response => response),
      catchError(GlobalService.handleError('Get Campaign  Stats', url))
    );
  }

  getCampaignDetails(id: number) {
    let url = devUrl + `/campaigns/${id}`;
    return this.http.get(url).pipe(
      map(response => response),
      catchError(GlobalService.handleError('Get Campaign Details', url))
    );
  }

  createCampaign(data) {
    let url = devUrl + "/campaigns";
    return this.http.post(url, data).pipe(
      map(response => response),
      catchError(GlobalService.handleError('Create Campaign', url))
    );
  }
  updateCampaign(data) {
    let url = devUrl + `/campaigns/${data.campaign_id}`;
    return this.http.post(url, data).pipe(
      map(response => response),
      catchError(GlobalService.handleError('Update Campaign', url))
    );
  }

  // Campaign Stages
  getStages(id: number) {
    let url = devUrl + `/campaigns/${id}/stages`;
    return this.http.get<{ status: "success" | "failure"; data: ICampaignStage[] }>(url).pipe(
      map(response => response),
      catchError(GlobalService.handleError('Get Stages', url))
    );
  }

  updateStage(id: number, stageId: number, data) {
    let url = devUrl + `/campaigns/${id}/stages/${stageId}`;
    return this.http.post(url, data).pipe(
      map(response => response),
      catchError(GlobalService.handleError('Update Stage', url))
    );
  }

  createStage(id: number, data) {
    let url = devUrl + `/campaigns/${id}/stages`;
    return this.http.post(url, data).pipe(
      map(response => response),
      catchError(GlobalService.handleError('Create Stage', url))
    );
  }

  // Campaign Targets
  getTargets() {
    let url = devUrl + "/targets";
    return this.http.get(url).pipe(
      map(response => response),
      catchError(GlobalService.handleError('Get Targets', url))
    );
  }

  getTargetDetails(targetId: number) {
    let url = devUrl + `/targets/${targetId}`;
    return this.http.get(url).pipe(
      map(response => response),
      catchError(GlobalService.handleError('Get Target Details', url))
    );
  }

  getCampaignTargets(id: number, data:any) {
    let url = devUrl + `/campaigns/${id}/targets`;
    url = url + '?' + this.objectToQueryString(data);
    return this.http.get(url).pipe(
      map(response => response),
      catchError(GlobalService.handleError('Get Campaign Targets', url))
    );
  }

  createTarget(id: number, data) {
    let url = devUrl + `/campaigns/${id}/targets`;
    return this.http.post(url, data).pipe(
      map(response => response),
      catchError(GlobalService.handleError('Create Target', url))
    );
  }

  updateTarget(id: number, camp_target_id: number, data) {
    let url = devUrl + `/campaigns/${id}/targets/${camp_target_id}`
    return this.http.post(url, data).pipe(
      map(response => response),
      catchError(GlobalService.handleError('Create Target', url))
    );
  }

  // Campaign Emails
  getEmailDetails(emailId: number) {
    let url = devUrl + `/emails/${emailId}`;
    return this.http.get(url).pipe(
      map(response => response),
      catchError(GlobalService.handleError('Get Email Details', url))
    );
  }

  getCampaignEmails(id: number) {
    let url = devUrl + `/campaigns/${id}/emails`;
    return this.http.get(url).pipe(
      map(response => response),
      catchError(GlobalService.handleError('Get Campaign Emails', url))
    );
  }

  getTargetEmails(id: number, targetId: number) {
    let url = devUrl + `/campaigns/${id}/targets/${targetId}/emails`;
    return this.http.get(url).pipe(
      map(response => response),
      catchError(GlobalService.handleError('getTargetEmails', url))
    );
  }

  getTargetCampaignEmail(campaign_target_id: number) {
    let url = devUrl + `/targets/${campaign_target_id}`;
    return this.http.get(url).pipe(
      map(response => response),
      catchError(GlobalService.handleError('getTargetCampaignEmail', url))
    );
  }

  getReliedEmails(email_id: string) {
    let url = devUrl + `/emails/${email_id}/replies`;
    return this.http.get(url).pipe(
      map(response => response),
      catchError(GlobalService.handleError('getReliedEmails', url))
    );
  }

  /**
   *
   * @param data
   * @returns
   */
  migrateCandidates(data) {
    const url = devUrl + '/campaigns/migrate';
    return this.http.post<any>(url, data).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('migrateCandidates', url)));
  }

  /**
   * Bulk Reveal api call
   * @param data Bulk Reveal paylaod
   * @returns
   */
  bulkReveal(data: BulkReveal){
    const url = devUrl + '/personsearch/revealcontact/bulk';
    return this.http.post<any>(url, data).pipe(
      map((response) => response),
      tap(() => { this.creditActivityService.notifyCreditConsumed(); }),
      catchError((err) => {
        this.creditActivityService.notifyCreditConsumed();
        return GlobalService.handleError('bulkReveal', url)(err);
      })
    );
  }

  /**
   * Function used to show the banner in listing when bulk reveal in process
   * @param campaign_id
   * @returns
   */
  bulkRevealStatus(campaign_id: number){
    let url = devUrl + `/campaigns/${campaign_id}/targets/bulk-reveal`;
    return this.http.get(url).pipe(
      map(response => response)
      // catchError(GlobalService.handleError('bulkRevealStatus', url))
    );
  }

  /**
   * Used to add contact details manually
   * @param camp_target_id Exist only when component called from email-campaign-slider
   * @param data payload
   * @returns
   */
  addContactManually(camp_target_id = null, data, campaign_id = null){
    let url = devUrl + ((camp_target_id) ?  `/targets/${camp_target_id}/contact${campaign_id ? `?campaign_id=${campaign_id}` : ''}` : `/candidate/contact`);
    return this.http.put<any>(url, data, {observe:'response'}).pipe(map((response)=>({...response, statusCode: response.status})), catchError((err: HttpErrorResponse) => throwError(err)));
  }

  /**
   * Fetch user-level sending limits and queued emails count
   * @param usernumber
   */
  fetchUserLimits(usernumber: string) {
    const url = devUrl + '/user/fetchlimits';
    const params = new HttpParams().set('usernumber', usernumber);
    return this.http.get(url, { params }).pipe(
      map(response => response),
      catchError(GlobalService.handleError('fetchUserLimits', url))
    );
  }

  /**
   * Update user-level sending limits
   * @param payload { usernumber, dailyLimit, hourlyLimit }
   */
  updateUserLimits(payload: any) {
    const url = devUrl + '/user/updatelimits';
    return this.http.post(url, payload).pipe(
      map(response => response),
      catchError(GlobalService.handleError('updateUserLimits', url))
    );
  }

  /**
   * Service used for unsubsubscibe
   */
  unsubscribe(data){
    const url = devUrl + `/candidate/unsubscribe`;
    return this.http.post<any>(url, data, {observe:'response'}).pipe(map((response)=>({...response, statusCode: response.status})), catchError((err: HttpErrorResponse) => throwError(err)));
  }

  /**
   * Reactivate campaigns with integration errors for a given integration ID.
   * Sends the original encrypted state token so the server can validate and update campaign statuses.
   * @param state Original encrypted state token (userId:integrationId:timestamp)
   */
  reactivateIntegrationErroredCampaigns(state: string) {
    const url = devUrl + '/campaigns/integration-errored-status';
    return this.http.post<any>(url, { state }).pipe(
      map(response => response),
      catchError(GlobalService.handleError('reactivateIntegrationErroredCampaigns', url))
    );
  }

  /**
   * Uploads attachments for a campaign email.
   */
  uploadAttachments(data: FormData): Observable<any> {
    const url = devUrl + "/attachment/upload";
    return this.http.post(url, data, { observe: 'response' }).pipe(
      map(response => ({ ...response, statusCode: response.status })),
      catchError((err: HttpErrorResponse) => throwError(err))
    );
  }


  /**
   * Priority of email that must be set in target_email.
   * @param contactData  contact detail object
   * @param type type of email personal / professional
   * @returns return the contactdetail having priority 1
   */
  setPriorityBasedTargetEmail(contactData, type: string) {
    let priorityArray = [];
    contactData.map(data => {
      if (data.contactdetailcategory === type) {
        if (data.enrichedvia === 'added-manually' && data.isprimary) {
          priorityArray.push({ priority: 1, data: data.contactdetail })
        } else if (data.enrichedvia === 'added-manually') {
          priorityArray.push({ priority: 2, data: data.contactdetail });
        } else if (data.isprimary) {
          priorityArray.push({ priority: 3, data: data.contactdetail })
        } else {
          priorityArray.push({ priority: 4, data: data.contactdetail });
        }
      }
    });
    if (priorityArray.length) {
      priorityArray.sort((a, b) => a.priority - b.priority);
      return priorityArray[0].data;
    } else return '';
  }

}
